import { AbilitySwitch, IfAbility } from '@efdigital/ll-addon-auth';
import { DataTypeXceed } from '@efdigital/ll-addon-data/data-types/xceed';
import type { Program } from '@efdigital/ll-addon-data/types/xceed/program';
import { Heading } from '@efdigital/ll-addon-ui';
import {
  EditContext,
  EditingStoppedReason,
} from '@efdigital/ll-admin-v2/components/edit-context.gts';
import { PaginationByOffset } from '@efdigital/ll-admin-v2/components/pagination.gts';
import { EditProgram } from '@efdigital/ll-admin-v2/components/programs/edit-program.gts';
import { ResultsTable } from '@efdigital/ll-admin-v2/components/results-table.gts';
import {
  SearchPrograms,
  SearchProgramsState,
} from '@efdigital/ll-admin-v2/components/search/search-programs';
import { formatTimestamp } from '@efdigital/ll-admin-v2/helpers/format-timestamp';
import { getPaginationTotal } from '@efdigital/ll-admin-v2/helpers/get-pagination-total';
import { generateRandomSlug } from '@efdigital/ll-admin-v2/lib/random-slug';
import type AbilityService from '@efdigital/ll-admin-v2/services/ability';
import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import { Await, Request } from '@warp-drive/ember';
import BsModal from 'ember-bootstrap/components/bs-modal';

export default class ProgramsRoute extends Component<{
  Args: {
    model: {
      id: string;
    };
  };
}> {
  @service declare readonly store: Store;
  @service declare readonly router: RouterService;
  @service declare readonly ability: AbilityService;
  @tracked state = new SearchProgramsState();
  @tracked modalOpen = false;
  @tracked newProgram?: Program;

  setState = (state: Partial<SearchProgramsState>) => {
    Object.assign(this.state, state);
  };

  showCreateModal = () => {
    this.newProgram = this.store.createRecord<Program>(DataTypeXceed.Program, {
      slug: generateRandomSlug(),
    });
    this.modalOpen = true;
  };

  hideCreateModal = () => {
    this.modalOpen = false;
    this.newProgram = undefined;
  };

  programCreated = (reason: EditingStoppedReason) => {
    if (reason === EditingStoppedReason.Saved) {
      const id = this.newProgram?.id;
      if (id) {
        this.router.transitionTo('programs.program.index', id);
        return;
      }
    }
    // On cancel or failed save, just close the modal and reset state
    this.hideCreateModal();
  };

  setOffset = (offset: number): void => {
    this.setState({ offset });
  };

  <template>
    <div class="container py-5">
      <Heading @text="Programs" />

      <AbilitySwitch @ability="programs">
        <:viewer>
          <SearchPrograms
            @legend="Program filters"
            @state={{this.state}}
            @setState={{this.setState}}
          >
            <:query as |query|>
              <Request @query={{query}}>
                <:content as |response|>
                  <PaginationByOffset
                    @offset={{this.state.offset}}
                    @limit={{this.state.limit}}
                    @total={{getPaginationTotal response}}
                    @onOffsetChange={{this.setOffset}}
                    class="d-flex flex-row justify-content-center my-4"
                  />

                  <ResultsTable @data={{response.data}}>
                    <:header>
                      <th scope="col">Title</th>
                      <th scope="col">Client</th>
                      <th scope="col">Modified</th>
                    </:header>
                    <:row as |program|>
                      <tr>
                        <th scope="row"><LinkTo
                            @route="programs.program.index"
                            @models={{array program.id}}
                          >{{program.title}}</LinkTo></th>
                        <td>
                          <Await @promise={{program.client}}>
                            <:success as |client|>
                              {{client.title}}
                            </:success>
                          </Await>
                        </td>
                        <td class="font-monospace">{{formatTimestamp
                            program.modified
                          }}</td>
                      </tr>
                    </:row>
                    <:empty>
                      <div class="alert alert-info" role="alert">No programs
                        found matching your filters.</div>
                    </:empty>
                  </ResultsTable>

                  <PaginationByOffset
                    @offset={{this.state.offset}}
                    @limit={{this.state.limit}}
                    @total={{getPaginationTotal response}}
                    @onOffsetChange={{this.setOffset}}
                    class="d-flex flex-row justify-content-center my-4"
                  />
                </:content>
                <:error as |error retry|>
                  <div class="alert alert-danger" role="alert">
                    <h4 class="alert-heading">Error Loading Programs</h4>
                    <p>
                      {{if
                        error.message
                        error.message
                        "There was a problem loading programs. Please try again."
                      }}
                    </p>
                    {{#if retry}}
                      <button
                        type="button"
                        class="btn btn-secondary"
                        {{on "click" retry.retry}}
                      >Retry</button>
                    {{/if}}
                  </div>
                </:error>
              </Request>
            </:query>
          </SearchPrograms>
        </:viewer>
        <:none>
          <div class="alert alert-warning" role="alert">
            <h4 class="alert-heading">Access Denied</h4>
            <p>You do not have permission to view programs.</p>
            <p class="mb-0">Please contact your administrator if you believe you
              should have access.</p>
          </div>
        </:none>
      </AbilitySwitch>

      <IfAbility @ability="programs" @atLeast="editor">
        <:if>
          <div class="d-flex justify-content-center my-5">
            <button
              type="button"
              class="btn btn-primary btn-md"
              {{on "click" this.showCreateModal}}
            >New Program</button>
          </div>

          <BsModal
            @open={{this.modalOpen}}
            @onHidden={{this.hideCreateModal}}
            @backdropClose={{false}}
            @size="xl"
            as |modal|
          >
            <modal.header>
              <h3 class="modal-title h4">Create Program</h3>
            </modal.header>
            <modal.body>
              {{#if this.newProgram}}
                <EditContext
                  @onEditingStopped={{this.programCreated}}
                  as |context|
                >
                  <EditProgram @item={{this.newProgram}} @context={{context}} />
                </EditContext>
              {{/if}}
            </modal.body>
          </BsModal>
        </:if>
      </IfAbility>
    </div>
  </template>
}
